import { setUser as setSentryUser } from '@sentry/nextjs'
import jwtDecode from 'jwt-decode'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getLoggedInUserToken } from 'utils'
import { create } from 'zustand'

import { useMarket } from '@/contexts/market-context/market-context'

export function useLoginUrl(unitId?: string) {
  const { language_code, country_code } = useMarket()
  const { protocol, host, href } = window.location
  const profileUrl = `${protocol}//${host}/${country_code}/${language_code}/profile`
  const [loginUrl, setLoginUrl] = useState<URL>(new URL(`${profileUrl}/login`))
  const [signupUrl, setSignupUrl] = useState<URL>(
    new URL(`${profileUrl}/signup`)
  )
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const regex = /\?.*/
    const redirectTo =
      searchParams.get('redirectTo') ??
      href.replace(regex, '/reservation-summary')
    loginUrl.searchParams.set(
      'from',
      redirectTo.includes('/reservation-summary')
        ? redirectTo
        : `${href}/reservation-summary`
    )
    signupUrl.searchParams.set(
      'from',
      redirectTo.includes('/reservation-summary')
        ? redirectTo
        : `${href}/reservation-summary`
    )
    if (unitId) {
      loginUrl.searchParams.set('stores', unitId)
      signupUrl.searchParams.set('stores', unitId)
    }
    setLoginUrl(loginUrl)
    setSignupUrl(signupUrl)
  }, [href, loginUrl, unitId, signupUrl, searchParams])

  return {
    loginUrl,
    signupUrl,
  }
}

const LOYALTY_PROGRAMS_FIELD = 'https://accounts.ikea.com/loyaltyPrograms'
const CUSTOMER_TYPE_FIELD = 'https://accounts.ikea.com/customerType'
const MEMBER_ID_FIELD = 'https://accounts.ikea.com/memberId'
const RETAIL_UNIT_FIELD = 'https://accounts.ikea.com/retailUnit'
const PARTY_UID_FIELD = 'https://accounts.ikea.com/partyUId'

type IkeaJwtPayload = {
  iss?: string
  sub?: string
  aud?: string[] | string
  exp?: number
  nbf?: number
  iat?: number
  jti?: string
  [LOYALTY_PROGRAMS_FIELD]?: string[]
  [CUSTOMER_TYPE_FIELD]?: string
  [MEMBER_ID_FIELD]?: string
  [RETAIL_UNIT_FIELD]?: string
  [PARTY_UID_FIELD]?: string
}
type User = {
  user_id?: string
  user_type?: string
  accessToken: string
}

type UserState = {
  user: User | null
  setUser: (user: User | null) => void
}

export const getCookie = (cookieName: string) => {
  if (typeof document === 'undefined') {
    return ''
  }
  const name = cookieName + '='
  const cookies = document?.cookie.split(';')
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i]
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1)
    }
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length, cookie.length)
    }
  }
  return ''
}

export const checkTokenExpirationTime = (jwtToken: string) => {
  const { exp } = jwtDecode<IkeaJwtPayload>(jwtToken)
  if (exp) {
    const currentTime = new Date().getTime()
    return currentTime > exp
  }
  return false
}

export const useUserStore = create<UserState>()((set) => ({
  user: null,
  setUser: (user: User | null) => set({ user }),
}))

export function useOptionalUser() {
  const { user, setUser } = useUserStore()
  const [isLoading, setIsLoading] = useState(true)
  const { country_code } = useMarket()

  const handleAccessToken = useCallback(
    (accessToken: string) => {
      if (accessToken.length > 0) {
        try {
          const jwtContent = jwtDecode<IkeaJwtPayload>(accessToken)
          setUser({
            user_id: jwtContent[PARTY_UID_FIELD],
            user_type: jwtContent[CUSTOMER_TYPE_FIELD],
            accessToken,
          })
          setSentryUser({
            id: jwtContent['https://accounts.ikea.com/partyUId'],
          })
        } catch (error) {
          setUser(null)
        }
      }
      setIsLoading(false)
    },
    [setUser]
  )

  useEffect(() => {
    // STUB: Return mock user data in development environments
    if (process.env.NODE_ENV === 'development') {
      const stubUser = {
        user_id: 'stub-user-123',
        user_type: 'IKEA_FAMILY_MEMBER',
        // accessToken: 'stub-access-token',
        accessToken: process.env.NEXT_PUBLIC_CTE_JWT!,
      }
      setUser(stubUser)
      setIsLoading(false)
      return
    }

    // Original authentication logic for non-development environments
    if (country_code === 'cn') {
      const accessToken = getCookie('accessToken')
      if (accessToken && checkTokenExpirationTime(accessToken)) {
        handleAccessToken(accessToken)
      }
    } else {
      // code to get access token from cookie
      // const accessToken = getCookie('idp_reguser')
      // if (accessToken && checkTokenExpirationTime(accessToken)) {
      //   handleAccessToken(accessToken)
      // }
      void getLoggedInUserToken().then((accessToken) => {
        handleAccessToken(accessToken)
      })
    }
  }, [handleAccessToken, country_code, setUser])

  return {
    user,
    isLoading,
  }
}

export function useRequiredUser() {
  const { user } = useOptionalUser()
  const { loginUrl } = useLoginUrl()

  // if the user is not logged in, redirect to the login page (except in development)
  if (!user) {
    if (process.env.NODE_ENV === 'development') {
      // Return stub user in development environments as fallback
      return {
        user_id: 'stub-user-123',
        user_type: 'IKEA_FAMILY_MEMBER',
        accessToken: 'stub-access-token',
      }
    }

    // Normal redirect behavior for non-development environments
    void loginUrl.searchParams.set('redirectTo', window.location.href)
    window.location.href = loginUrl.toString()
    throw new Error(
      `user should be logged in on this page: ${window.location.toString()}`
    )
  }

  return user
}
